/**
 * Timeline Carousel
 * A carousel with year/number navigation showing adjacent slides with reduced opacity
 */
class TimelineCarousel {
  constructor(container) {
    this.container = container;
    this.slides = Array.from(container.querySelectorAll('.timeline-carousel__slide'));
    this.navItems = Array.from(container.querySelectorAll('.timeline-carousel__nav-item'));
    this.descriptions = Array.from(container.querySelectorAll('.timeline-carousel__description'));
    this.prevButton = container.querySelector('.timeline-carousel__arrow--prev');
    this.nextButton = container.querySelector('.timeline-carousel__arrow--next');
    
    this.currentIndex = 0;
    this.totalSlides = this.slides.length;
    
    if (this.totalSlides === 0) return;
    
    this.init();
  }
  
  init() {
    this.setupNavigation();
    this.setupKeyboardNavigation();
    this.setupSwipeSupport();
    this.createLiveRegion();
    this.updateCarousel();
  }
  
  /**
   * Setup click handlers for navigation
   */
  setupNavigation() {
    // Arrow buttons
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => this.navigate(-1));
    }
    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.navigate(1));
    }
    
    // Number/year nav items
    this.navItems.forEach((item, index) => {
      item.addEventListener('click', () => this.goToSlide(index));
    });
    
    // Click on adjacent slides to navigate
    this.slides.forEach((slide, index) => {
      slide.addEventListener('click', () => {
        if (index !== this.currentIndex) {
          this.goToSlide(index);
        }
      });
    });
  }
  
  /**
   * Setup keyboard navigation
   */
  setupKeyboardNavigation() {
    this.container.addEventListener('keydown', (e) => {
      switch (e.key) {
        case 'ArrowLeft':
        case 'Left':
          e.preventDefault();
          this.navigate(-1);
          break;
        case 'ArrowRight':
        case 'Right':
          e.preventDefault();
          this.navigate(1);
          break;
        case 'Home':
          e.preventDefault();
          this.goToSlide(0);
          break;
        case 'End':
          e.preventDefault();
          this.goToSlide(this.totalSlides - 1);
          break;
      }
    });
    
    // Make container focusable
    if (!this.container.hasAttribute('tabindex')) {
      this.container.setAttribute('tabindex', '0');
    }
  }
  
  /**
   * Setup touch/swipe support for mobile
   */
  setupSwipeSupport() {
    let touchStartX = 0;
    let touchEndX = 0;
    const swipeThreshold = 50;
    
    this.container.addEventListener('touchstart', (e) => {
      touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });
    
    this.container.addEventListener('touchend', (e) => {
      touchEndX = e.changedTouches[0].screenX;
      const swipeDistance = touchStartX - touchEndX;
      
      if (Math.abs(swipeDistance) > swipeThreshold) {
        if (swipeDistance > 0) {
          this.navigate(1); // Swipe left = next
        } else {
          this.navigate(-1); // Swipe right = prev
        }
      }
    }, { passive: true });
  }
  
  /**
   * Navigate by direction
   */
  navigate(direction) {
    this.goToSlide(this.currentIndex + direction);
  }
  
  /**
   * Go to specific slide index
   */
  goToSlide(index) {
    // Wrap around
    if (index < 0) {
      index = this.totalSlides - 1;
    } else if (index >= this.totalSlides) {
      index = 0;
    }
    
    this.currentIndex = index;
    this.updateCarousel();
  }
  
  /**
   * Update carousel display with circular positioning
   */
  updateCarousel() {
    // Calculate prev/next with wrap-around for circular effect
    const prevIndex = (this.currentIndex - 1 + this.totalSlides) % this.totalSlides;
    const nextIndex = (this.currentIndex + 1) % this.totalSlides;
    
    // Update slides with position classes for circular layout
    this.slides.forEach((slide, index) => {
      slide.classList.remove(
        'timeline-carousel__slide--active',
        'timeline-carousel__slide--adjacent',
        'timeline-carousel__slide--prev',
        'timeline-carousel__slide--next',
        'timeline-carousel__slide--hidden'
      );
      
      if (index === this.currentIndex) {
        slide.classList.add('timeline-carousel__slide--active');
        slide.setAttribute('aria-hidden', 'false');
        slide.style.order = '2';
      } else if (index === prevIndex) {
        slide.classList.add('timeline-carousel__slide--adjacent', 'timeline-carousel__slide--prev');
        slide.setAttribute('aria-hidden', 'true');
        slide.style.order = '1';
      } else if (index === nextIndex) {
        slide.classList.add('timeline-carousel__slide--adjacent', 'timeline-carousel__slide--next');
        slide.setAttribute('aria-hidden', 'true');
        slide.style.order = '3';
      } else {
        slide.classList.add('timeline-carousel__slide--hidden');
        slide.setAttribute('aria-hidden', 'true');
        slide.style.order = '99';
      }
    });
    
    // Update navigation items
    this.navItems.forEach((item, index) => {
      item.classList.toggle('timeline-carousel__nav-item--active', index === this.currentIndex);
      item.setAttribute('aria-selected', index === this.currentIndex);
    });
    
    // Update descriptions
    this.updateDescriptions();
    
    // Announce change
    this.announceChange();
  }
  
  /**
   * Update descriptions based on current slide
   */
  updateDescriptions() {
    this.descriptions.forEach((desc, index) => {
      const descIndex = parseInt(desc.dataset.descriptionIndex, 10);
      if (descIndex === this.currentIndex) {
        desc.classList.add('timeline-carousel__description--active');
      } else {
        desc.classList.remove('timeline-carousel__description--active');
      }
    });
  }
  
  /**
   * Create ARIA live region
   */
  createLiveRegion() {
    const liveRegion = document.createElement('div');
    liveRegion.className = 'visually-hidden';
    liveRegion.setAttribute('aria-live', 'polite');
    liveRegion.setAttribute('aria-atomic', 'true');
    this.container.appendChild(liveRegion);
    this.liveRegion = liveRegion;
  }
  
  /**
   * Announce slide change to screen readers
   */
  announceChange() {
    if (!this.liveRegion) return;
    
    const activeNavItem = this.navItems[this.currentIndex];
    const label = activeNavItem ? activeNavItem.textContent.trim() : '';
    
    this.liveRegion.textContent = `Slide ${this.currentIndex + 1} of ${this.totalSlides}${label ? ': ' + label : ''}`;
  }
}

// Initialize all timeline carousels
document.addEventListener('DOMContentLoaded', function() {
  const carousels = document.querySelectorAll('.timeline-carousel');
  
  carousels.forEach((carousel) => {
    try {
      new TimelineCarousel(carousel);
    } catch (error) {
      console.error('Failed to initialize timeline carousel:', error);
    }
  });
});

// Support for Shopify Theme Editor
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', function(event) {
    const carousel = event.target.querySelector('.timeline-carousel');
    if (carousel) {
      new TimelineCarousel(carousel);
    }
  });
}

